import { Editor, JSONContent } from "@tiptap/react";

import { GRAPHIC_ORGANIZER_PARAM } from "@/components/AIComponents/ExportablesModal";
import LessonPlanPDF from "@/components/LessonPlanPDF";
import { generatePDF, PdfOptions } from "@/utils/generatePDF";
import { ILessonPlanNode } from "./useLessonPlanEditor/useLessonPlanEditor";

export async function generateLessonPlanPDF(
  articleShortTitle: string,
  htmlString: string,
  pdfOptions: PdfOptions = {},
): Promise<Blob> {
  const defaultOptions: PdfOptions = {
    margin: [20, 20, 20, 20],
    format: "letter",
    orientation: "portrait",
    filename: `Lesson Plan - ${articleShortTitle}.pdf`,
    pagebreak: {
      /* avoids page-breaks on these elements */
      avoid: ["h1", "h2", "h3", "li", "p"],
    },
    image: { type: "jpeg", quality: 1 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
    },
    jsPDF: {
      unit: "mm",
      format: "letter",
      orientation: "portrait",
      autoPaging: "text",
    },
  };

  const mergedOptions = { ...defaultOptions, ...pdfOptions };

  try {
    const pdfBlob = await generatePDF(
      LessonPlanPDF,
      { articleShortTitle, htmlString },
      mergedOptions,
    );

    return pdfBlob;
  } catch (error) {
    console.error("Error generating lesson plan PDF:", error);
    throw new Error("Failed to generate lesson plan PDF");
  }
}

export function getDownloadHandler({
  title,
  editor,
}: {
  title: string;
  editor: Editor | null;
}) {
  return async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    try {
      // Reverting custom nodes temporary into link marks so they
      // can be treated as normal links
      editor?.commands.setContent(
        formatLessonPlan({
          lessonPlan: editor?.getJSON(),
          isEditOrDownloadMode: true,
        }),
      );

      const htmlString = editor?.getHTML() || "";
      const pdf = await generateLessonPlanPDF(title, htmlString);
      const file = new Blob([pdf], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      // Create a link element to trigger the download
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = `Lesson Plan - ${title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);

      // Reverting link marks back into custom nodes so they
      // can open the graphic organizer modal on click
      editor?.commands.setContent(
        formatLessonPlan({ lessonPlan: editor?.getJSON() }),
      );
    } catch (error) {
      console.error("Error downloading lesson plan PDF:", error);
      throw error;
    }
  };
}

export function getCopyHandler({
  editor,
  onFulfilled,
  onRejected,
}: {
  editor: Editor | null;
  onFulfilled?: (props: { rich: string; plain: string }) => void;
  onRejected?: (error: any) => void;
}) {
  return async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const rich = editor?.getHTML() || "";
    const plain = editor?.getText() || "";

    let successful: boolean;
    let error: any;

    try {
      if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
        // Clipboard API
        const html = new Blob([rich], { type: "text/html" });
        const text = new Blob([plain], { type: "text/plain" });
        const data = new ClipboardItem({
          "text/html": html,
          "text/plain": text,
        });
        await navigator.clipboard.write([data]);
        successful = true;
      } else {
        // Fallback using document.execCommand
        function listener(e: ClipboardEvent) {
          e.clipboardData?.setData("text/html", rich);
          e.clipboardData?.setData("text/plain", plain);
          e.preventDefault();
        }
        document.addEventListener("copy", listener);
        successful = document.execCommand("copy");
        document.removeEventListener("copy", listener);

        if (!successful) {
          throw new Error("Fallback copy failed");
        }
      }
    } catch (e) {
      successful = false;
      error = e;
    }

    if (successful) {
      if (onFulfilled) onFulfilled({ plain, rich });
    } else {
      console.error("Failed to copy Lesson Plan to clipboard", error);
      if (onRejected) {
        onRejected(error);
      }
    }
  };
}

export function getUrlFromNodeAttrs({
  attrs,
}: {
  attrs?: { [key: string]: any };
}) {
  const hrefAttr = attrs?.["href"] || "";
  const href = hrefAttr.startsWith("http")
    ? hrefAttr
    : `${window.location.origin}${window.location.pathname}?${hrefAttr.replace(/^[?/]*/, "")}`;
  return new URL(href);
}

export function formatLessonPlan({
  lessonPlan,
  isEditOrDownloadMode = false,
}: {
  lessonPlan: ILessonPlanNode | JSONContent | null | undefined;
  isEditOrDownloadMode?: boolean;
}) {
  const node = lessonPlan as JSONContent | null | undefined;
  if (isEditOrDownloadMode) {
    // Format the lesson plan for editing or download
    return convertGraphicOrganizerNode(node, "toMark");
  } else {
    // Format the lesson plan for display
    return convertGraphicOrganizerNode(node, "toCustomNode");
  }
}

/**
 * Converts between `link` marks with a graphic organizer href
 * and custom `graphic_organizer_link` nodes, depending on the direction.
 *
 * - When `direction = "toNode"`:
 *   Recursively traverses the document tree and replaces text nodes
 *   containing a `link` mark whose `href` starts with GRAPHIC_ORGANIZER_PARAM
 *   with a `graphic_organizer_link` node wrapping the text.
 *
 * - When `direction = "toMark"`:
 *   Recursively traverses the document tree and replaces
 *   `graphic_organizer_link` nodes with equivalent text nodes
 *   containing a `link` mark pointing to the same `href`.
 *
 * This ensures a reversible transformation between the two representations,
 * while preserving the rest of the document structure.
 *
 * @param originalNode The root node (or subtree) to transform.
 * @param direction    The transformation direction: `"toNode"` or `"toMark"`.
 * @returns            A new node with the transformation applied, or null if the input was null.
 */
export const convertGraphicOrganizerNode = (
  originalNode: { type?: string; content?: JSONContent[] } | undefined | null,
  direction: "toCustomNode" | "toMark",
): JSONContent | null => {
  if (!originalNode) return null;
  const node: JSONContent = { ...originalNode };

  if (
    (direction === "toCustomNode" && node.type !== "text") ||
    (direction === "toMark" && node.type !== "graphic_organizer_link")
  ) {
    if (node.content) {
      node.content = node.content
        .map((child) => convertGraphicOrganizerNode(child, direction))
        .filter((child): child is JSONContent => child !== null);
    }
    return node;
  }

  if (direction === "toCustomNode") {
    const graphicOrganizerMark = node.marks?.find(
      ({ type, attrs }) =>
        type === "link" &&
        `${attrs?.["href"] || ""}`.includes(`${GRAPHIC_ORGANIZER_PARAM}=`),
    );

    if (!graphicOrganizerMark) return node;

    const url = getUrlFromNodeAttrs(graphicOrganizerMark);
    const href = url.toString();

    return {
      type: "graphic_organizer_link",
      attrs: { href },
      content: [{ type: "text", text: node.text || "" }],
    };
  }

  if (direction === "toMark" && node.type === "graphic_organizer_link") {
    const url = getUrlFromNodeAttrs(node);
    const href = url.toString();

    return {
      type: "text",
      text: node.content?.[0]?.text ?? "",
      marks: [
        {
          type: "link",
          attrs: { href },
        },
      ],
    };
  }

  return node;
};
